import {
  RI_BREAKDOWNS,
  type RiBreakdown,
} from 'frontend-utils/utils/constants/constant-values';
import type {
  RevenueInsightSegmentationValue,
  ReviRemainingElement,
} from 'lumen/models';
import type { SegmentationValueLike } from 'lumen/models/revenue-insight/segmentation-value';
import {
  DUMMY_CALCULATED_VALUES,
  DUMMY_CALCULATED_VALUES_NO_CHANGE,
  DUMMY_SEGMENTATION_VALUE,
  EMPTY_SEGMENTATION_VALUE,
} from 'lumen/utils/constants/ri-constant-values';

/**
 * Filters out segmentation value ids that are already present in the current filters.
 * This prevents duplicate entries when merging filtered and unfiltered responses.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function filterRemainingValues(
  remainingValues: string[] | null,
  currentFilters: string[] | undefined,
) {
  if (!remainingValues) {
    return null;
  }

  if (!currentFilters || currentFilters.length === 0) {
    return remainingValues;
  }

  const currentFiltersSet = new Set(currentFilters);

  return remainingValues.filter((value) => !currentFiltersSet.has(value));
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function createDummyItemFromUnfilteredItem(
  unfilteredItem: RevenueInsightSegmentationValue | ReviRemainingElement,
  currentFilters?: string[],
) {
  let calculatedValues = { ...DUMMY_CALCULATED_VALUES };
  let reference = { ...EMPTY_SEGMENTATION_VALUE };

  if (unfilteredItem.referenceValue === null) {
    calculatedValues = { ...DUMMY_CALCULATED_VALUES_NO_CHANGE };
    reference = { ...DUMMY_SEGMENTATION_VALUE };
  }

  // If there are no total rooms, we can assume the value is completely mssing (otherwise it would be 0)
  // We can't simply check value === null since we overwrite this value in the mapping step
  if (unfilteredItem.value?.nrOfRoomsTotal === null) {
    calculatedValues.BOB = { ...DUMMY_SEGMENTATION_VALUE };
    calculatedValues.cancellationBOB = { ...DUMMY_SEGMENTATION_VALUE };
    calculatedValues.grossBOB = { ...DUMMY_SEGMENTATION_VALUE };
  }

  // If the unfiltered item has a target, set the value and reference to 0 instead of N/A
  if ('budgetValue' in unfilteredItem && unfilteredItem.budgetValue) {
    calculatedValues.budget = { ...EMPTY_SEGMENTATION_VALUE };
    calculatedValues.budgetChange = { ...EMPTY_SEGMENTATION_VALUE };
  }
  if ('forecastValue' in unfilteredItem && unfilteredItem.forecastValue) {
    calculatedValues.forecast = { ...EMPTY_SEGMENTATION_VALUE };
    calculatedValues.forecastChange = { ...EMPTY_SEGMENTATION_VALUE };
  }

  const remainingValues =
    'remainingValues' in unfilteredItem ? unfilteredItem.remainingValues : null;

  return {
    referenceValue: reference,
    segmentationValueId:
      'segmentationValueId' in unfilteredItem
        ? unfilteredItem.segmentationValueId
        : undefined,
    value: EMPTY_SEGMENTATION_VALUE,
    cancellationValue: EMPTY_SEGMENTATION_VALUE,
    cancellationReferenceValue: EMPTY_SEGMENTATION_VALUE,
    unfiltered_data: unfilteredItem,
    segmentationValue:
      'segmentationValue' in unfilteredItem
        ? unfilteredItem.segmentationValue
        : undefined,
    segmentationId: unfilteredItem.segmentationId,
    id: unfilteredItem.id,
    filled_in_by_frontend: true,
    parentId: unfilteredItem.parentId,
    displayValue: unfilteredItem.displayValue,
    calculatedValues,
    isSelectable: unfilteredItem.isSelectable,
    remainingValues: filterRemainingValues(remainingValues, currentFilters),
    isAggregatedRow:
      'isAggregatedRow' in unfilteredItem
        ? unfilteredItem.isAggregatedRow
        : false,
    meta: 'meta' in unfilteredItem ? unfilteredItem.meta : undefined,
  };
}

export type SegmentationDict = {
  [key in RiBreakdown]?: Record<
    string,
    SegmentationValueLike | ReviRemainingElement
  >;
};

/**
 * Adds dummy elements to the filtered segmentation dict for values that exist in the unfiltered response
 * but are missing in the filtered response. This ensures consistent data display regardless of filtering.
 */

export function addDummyElementToFilteredSegmentationDict(
  filteredSegmentationDict: SegmentationDict,
  unfilteredItem: RevenueInsightSegmentationValue | ReviRemainingElement,
  currentFilters?: string[],
): void {
  if (
    !unfilteredItem.segmentationId ||
    filteredSegmentationDict[unfilteredItem.segmentationId]?.[
      unfilteredItem.id!
    ]
  ) {
    return;
  }

  const dummy = createDummyItemFromUnfilteredItem(
    unfilteredItem,
    currentFilters,
  );

  const { segmentationId } = unfilteredItem;

  if (
    segmentationId &&
    filteredSegmentationDict[segmentationId] &&
    unfilteredItem.id
  ) {
    filteredSegmentationDict[segmentationId][unfilteredItem.id] = dummy;
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSegmentationIdValidations(segmentationId: RiBreakdown) {
  const isKeyPickup = segmentationId === RI_BREAKDOWNS.PICKUP;
  const isKeyWeekday = segmentationId === RI_BREAKDOWNS.WEEKDAY;
  const isKeyNotPickupAndPace =
    !isKeyPickup && segmentationId !== RI_BREAKDOWNS.PACE;
  const isKeyNotPickupAndWeekday = !isKeyPickup && !isKeyWeekday;
  const isKeyNotPaceAndStayDate =
    segmentationId !== RI_BREAKDOWNS.PACE &&
    segmentationId !== RI_BREAKDOWNS.IMPACT_DATE;

  return {
    isKeyPickup,
    isKeyWeekday,
    isKeyNotPickupAndPace,
    isKeyNotPickupAndWeekday,
    isKeyNotPaceAndStayDate,
  } as const;
}
